import { css, Theme } from "@emotion/react";

export const getBlockNoteEditorStyles = (theme: Theme, sideMenu: boolean) => css`
  .bn-container {
    display: flex;
    flex-direction: column-reverse;
    gap: 8px;
    max-width: 960px;
  }

  /* Theme vars live on .bn-root, which wraps both the editor and the floating-UI portal.
     .bn-container covers only the editor, so menus/toolbars would miss these. */
  .bn-root {
    --bn-colors-editor-text: ${theme.colors.sysTextDefault};
    --bn-colors-editor-background: ${theme.colors.sysBackgroundDefault};
    --bn-colors-menu-text: ${theme.colors.sysTextDefault};
    --bn-colors-menu-background: ${theme.colors.sysBackgroundDefault};
    --bn-colors-tooltip-text: ${theme.colors.sysTextDefault};
    --bn-colors-tooltip-background: ${theme.colors.sysBackgroundInactive};
    --bn-colors-hovered-text: ${theme.colors.sysTextDefault};
    --bn-colors-hovered-background: ${theme.colors.sysBackgroundInactive};
    --bn-colors-selected-text: ${theme.colors.sysTextWhite};
    --bn-colors-selected-background: ${theme.colors.sysTextDefault};
    --bn-colors-disabled-text: ${theme.colors.sysTextInactive};
    --bn-colors-disabled-background: ${theme.colors.sysBackgroundInactive};
    --bn-colors-shadow: ${theme.colors.sysBorderDecorative};
    --bn-colors-border: ${theme.colors.sysBorderDecorative};
    --bn-colors-side-menu: ${theme.colors.sysBorderDecorative};

    --bn-colors-highlights-gray-text: ${theme.colors.sysTextSecondary};
    --bn-colors-highlights-gray-background: ${theme.colors.sysBackgroundInactive};
    --bn-colors-highlights-red-text: ${theme.colors.sysDestructiveDefault};
    --bn-colors-highlights-red-background: ${theme.colors.sysDestructiveSubtleAlt};
    --bn-colors-highlights-yellow-text: ${theme.colors.sysWarningDefault};
    --bn-colors-highlights-yellow-background: ${theme.colors.sysWarningSubtleAlt};
    --bn-colors-highlights-green-text: ${theme.colors.sysSuccessDefault};
    --bn-colors-highlights-green-background: ${theme.colors.sysSuccessSubtleAlt};
    --bn-colors-highlights-blue-text: ${theme.colors.sysInfoDefault};
    --bn-colors-highlights-blue-background: ${theme.colors.sysInfoSubtleAlt};
    --bn-colors-highlights-purple-text: ${theme.colors.sysEditorHighlightPurpleText};
    --bn-colors-highlights-purple-background: ${theme.colors.sysEditorHighlightPurpleBackground};
  }

  .bn-editor {
    padding: ${sideMenu ? "0 0 0 24px" : "0"};
    flex: 1;
    font-family: inherit;
    font-size: 14px;
    max-width: 960px;
  }

  .bn-editor:focus-within {
    outline: none;
  }

  .bn-block-content {
    padding: 0;
  }

  /* Custom: Hide vertical lines in nested bullet points */
  .bn-block-group .bn-block-group > .bn-block-outer:not([data-prev-depth-changed]):before {
    border-left: none !important;
  }

  /* The toolbar now renders inside the .bn-root portal, not as a direct child of
     .bn-container, so this is a descendant selector rather than a child one. */
  .bn-root .bn-formatting-toolbar {
    margin-inline: auto;
    margin-bottom: 16px;
    display: flex;
    justify-content: center;
    width: 100%;
    box-shadow: none;
    flex-wrap: wrap;
  }

  /* Hide the plus button when side menu is enabled */
  ${sideMenu &&
  css`
    button[aria-label="Add block"] {
      display: none;
    }

    /* Also hide by data attribute selector */
    svg[data-test="dragHandleAdd"] {
      display: none;
    }

    /* Hide parent button containing the add icon */
    .bn-button:has(svg[data-test="dragHandleAdd"]) {
      display: none;
    }
  `}

  /* Note: This positioning for the side menu is necessary because of the way the side menu is implemented */
  /* This is necessary because of the custom font-size we've set for the editor */
  ${sideMenu &&
  css`
    .bn-side-menu {
      margin-top: -4px;
      margin-right: 4px;
      transform: translateY(-2px);
    }

    button[aria-label="Open block menu"] {
      transform: translateY(2px);
    }
  `}
`;
